import embeddedRuntimeSites from "./embedded-runtime-sites.json";
import { getDirectWebRuntimeOrigin } from "../runtime-shell";

// 发布包只维护一份预埋站点池，API 与官网门户必须始终切换到同一个站点域名。
export const EMBEDDED_RUNTIME_SITE_ORIGINS = Object.freeze([
  ...embeddedRuntimeSites.siteOrigins,
]);
export const EMBEDDED_RUNTIME_API_ORIGINS = Object.freeze(
  EMBEDDED_RUNTIME_SITE_ORIGINS.map((origin) => `${origin}/api`),
);
export const EMBEDDED_RUNTIME_FORUM_ORIGINS = EMBEDDED_RUNTIME_SITE_ORIGINS;
export const RUNTIME_ENDPOINTS_CHANGE_EVENT =
  "apk-games:runtime-endpoints-change";

const RUNTIME_ENDPOINT_PATH = "/v1/client/runtime-endpoints";
const RUNTIME_ENDPOINT_CACHE_KEY = "apk-games:runtime-endpoints:v6";
const LEGACY_RUNTIME_ENDPOINT_CACHE_KEY = "apk-games:runtime-endpoints:v5";
const DISCOVERY_TIMEOUT_MS = 2_800;
const MAX_PARALLEL_PROBES = 6;
const MAX_BACKEND_CANDIDATES = 64;
const MAX_CACHED_CANDIDATES =
  MAX_BACKEND_CANDIDATES + EMBEDDED_RUNTIME_SITE_ORIGINS.length + 1;
// 后台候选合法优先级最大为 1000；预埋和打包入口固定放在下一层，只在后台线路不可用时兜底。
const EMBEDDED_FALLBACK_PRIORITY = 1001;

export type RuntimeEndpointKind = "api" | "forum";

export interface RuntimeEndpointCandidate {
  origin: string;
  priority: number;
}

export interface RuntimeEndpointManifest {
  version: number;
  api_origins: RuntimeEndpointCandidate[];
  forum_origins: RuntimeEndpointCandidate[];
}

interface RuntimeEndpointCache {
  manifest: RuntimeEndpointManifest;
  selectedApiOrigin: string;
  selectedForumOrigin: string;
  measuredAt: number;
}

interface RuntimeEndpointState {
  dynamicSelection: boolean;
  initialApiOrigin: string;
  initialForumOrigin: string;
  initialForumBaseUrl: string;
  selectedApiOrigin: string;
  selectedForumOrigin: string;
  manifest?: RuntimeEndpointManifest;
  measuredAt: number;
}

type RuntimeEndpointBridge = {
  updateTrustedForumOrigins?: (originsJSON: string) => void;
};

let state: RuntimeEndpointState = {
  dynamicSelection: true,
  initialApiOrigin: EMBEDDED_RUNTIME_API_ORIGINS[0],
  initialForumOrigin: EMBEDDED_RUNTIME_FORUM_ORIGINS[0],
  initialForumBaseUrl: EMBEDDED_RUNTIME_FORUM_ORIGINS[0],
  selectedApiOrigin: EMBEDDED_RUNTIME_API_ORIGINS[0],
  selectedForumOrigin: EMBEDDED_RUNTIME_FORUM_ORIGINS[0],
  measuredAt: 0,
};
let readyPromise: Promise<void> = Promise.resolve();
let refreshPromise: Promise<void> | undefined;
let startupProbes: RuntimeEndpointProbe[] | undefined;
const failedOriginsUntil = new Map<string, number>();

export function initializeRuntimeEndpoints(input: {
  apiOrigin: string;
  forumOrigin: string;
}) {
  const directWebOrigin = getDirectWebRuntimeOrigin();
  const dynamicSelection = !directWebOrigin;
  const initialApiOrigin = directWebOrigin
    ? `${directWebOrigin}/api`
    : normalizeRuntimeApiBaseUrl(input.apiOrigin) ??
      EMBEDDED_RUNTIME_API_ORIGINS[0];
  const initialForumBaseUrl =
    directWebOrigin ??
    normalizeForumBaseUrl(input.forumOrigin) ??
    EMBEDDED_RUNTIME_FORUM_ORIGINS[0];
  const initialForumOrigin = new URL(initialForumBaseUrl).origin;
  state = {
    dynamicSelection,
    initialApiOrigin,
    initialForumOrigin,
    initialForumBaseUrl,
    selectedApiOrigin: initialApiOrigin,
    selectedForumOrigin: initialForumOrigin,
    measuredAt: 0,
  };
  startupProbes = undefined;
  if (!dynamicSelection) {
    // 普通线上 H5 必须保持同源：不读线路缓存、不探测后台清单，也不在请求失败后切域。
    readyPromise = Promise.resolve();
    return;
  }
  // 每次启动先用“预埋 + 上次动态缓存”做健康选举，首个业务请求不会盲信上次选中值。
  readyPromise = Promise.resolve().then(async () => {
    restoreRuntimeEndpointCache();
    syncNativeTrustedForumOrigins();
    startupProbes = await electCachedRuntimeEndpoints();
  });
  // 缓存选举完成后，复用探测响应拉取 Admin 最新清单，与预埋线路合并后覆盖本地缓存。
  void readyPromise
    .then(() => refreshRuntimeEndpoints())
    .catch(() => undefined);
}

export function ensureRuntimeEndpointsReady() {
  return readyPromise;
}

export function getSelectedRuntimeEndpoint(kind: RuntimeEndpointKind) {
  if (kind === "api") return state.selectedApiOrigin;
  // 显式配置的社区子路径仍作为门户首页保留，动态候选清单只管理 origin。
  return state.selectedForumOrigin === state.initialForumOrigin
    ? state.initialForumBaseUrl
    : state.selectedForumOrigin;
}

export function getTrustedForumOrigins() {
  if (!state.dynamicSelection) return [state.initialForumOrigin];
  return mergeCandidates(
    normalizeRuntimeOrigin,
    EMBEDDED_RUNTIME_FORUM_ORIGINS.map((origin) => ({ origin, priority: EMBEDDED_FALLBACK_PRIORITY })),
    [{ origin: state.initialForumOrigin, priority: EMBEDDED_FALLBACK_PRIORITY }],
    filterBackendCandidates(
      state.manifest?.forum_origins ?? [],
      EMBEDDED_RUNTIME_FORUM_ORIGINS,
      normalizeRuntimeOrigin,
    ),
  ).map((item) => item.origin);
}

export function isTrustedRuntimeForumOrigin(origin: string) {
  const normalized = normalizeRuntimeOrigin(origin);
  return Boolean(normalized && getTrustedForumOrigins().includes(normalized));
}

export async function refreshRuntimeEndpoints(excludedOrigins: string[] = []) {
  if (!state.dynamicSelection) return;
  if (refreshPromise) {
    await refreshPromise;
    return;
  }
  const exclusions = activeExclusions(excludedOrigins);
  refreshPromise = refreshRuntimeEndpointsInternal(exclusions).finally(() => {
    refreshPromise = undefined;
  });
  await refreshPromise;
}

export async function recoverRuntimeEndpoint(
  kind: RuntimeEndpointKind,
  failedOrigin: string,
) {
  if (!state.dynamicSelection) return undefined;
  const normalize = kind === "api" ? normalizeRuntimeApiBaseUrl : normalizeRuntimeOrigin;
  const normalizedFailed = normalize(failedOrigin) ?? failedOrigin;
  failedOriginsUntil.set(normalizedFailed, Date.now() + 60_000);
  await refreshRuntimeEndpoints([normalizedFailed]);
  let selected = getSelectedRuntimeEndpoint(kind);
  if (normalize(selected) === normalizedFailed) {
    // 后台刷新恰好先于失败标记启动时再执行一次，确保本次恢复排除故障线路。
    await refreshRuntimeEndpoints([normalizedFailed]);
    selected = getSelectedRuntimeEndpoint(kind);
  }
  return normalize(selected) !== normalizedFailed
    ? selected
    : undefined;
}

export function syncNativeTrustedForumOrigins() {
  if (typeof window === "undefined") return;
  const bridge = (
    window as typeof window & { GGEndpointBridge?: RuntimeEndpointBridge }
  ).GGEndpointBridge;
  try {
    bridge?.updateTrustedForumOrigins?.(
      JSON.stringify(getTrustedForumOrigins()),
    );
  } catch {
    // 浏览器和未安装新版原生桥的 APK 没有该接口，JS 运行时仍可正常选举。
  }
}

function activeExclusions(excludedOrigins: string[]) {
  const exclusions = new Set(excludedOrigins);
  const now = Date.now();
  for (const [origin, failedUntil] of failedOriginsUntil) {
    if (failedUntil > now) exclusions.add(origin);
    else failedOriginsUntil.delete(origin);
  }
  return exclusions;
}

function restoreRuntimeEndpointCache() {
  const cached = readRuntimeEndpointCache();
  if (!cached || !validateCachedManifest(cached.manifest)) return;
  // v5 只缓存后台原始清单；v6 也在读取时重新合并本地预埋，防止缓存被手动删掉固定入口。
  state.manifest = mergeManifestWithLocalFallbacks(cached.manifest);
  state.measuredAt = cached.measuredAt;
  if (
    state.manifest.api_origins.some(
      (item) => item.origin === cached.selectedApiOrigin,
    )
  ) {
    state.selectedApiOrigin = cached.selectedApiOrigin;
  }
  if (
    state.manifest.forum_origins.some(
      (item) => item.origin === cached.selectedForumOrigin,
    )
  ) {
    state.selectedForumOrigin = cached.selectedForumOrigin;
  }
}

async function electCachedRuntimeEndpoints() {
  const apiCandidates = currentApiCandidates();
  const forumCandidates = currentForumCandidates();
  const probes = await probeCandidates(apiCandidates);
  if (!state.dynamicSelection) return [];
  const selectedApi = pickHealthyRuntimeEndpoint(
    apiCandidates,
    new Map(probes.map((item) => [item.origin, item])),
    state.selectedApiOrigin,
  );
  commitSelectedApiOrigin(selectedApi, forumCandidates);
  publishRuntimeEndpointSelection();
  persistRuntimeEndpointCache();
  return probes;
}

function currentApiCandidates(excludedOrigins: ReadonlySet<string> = new Set()) {
  return mergeCandidates(
    normalizeRuntimeApiBaseUrl,
    EMBEDDED_RUNTIME_API_ORIGINS.map((origin) => ({ origin, priority: EMBEDDED_FALLBACK_PRIORITY })),
    [{ origin: state.initialApiOrigin, priority: EMBEDDED_FALLBACK_PRIORITY }],
    filterBackendCandidates(
      state.manifest?.api_origins ?? [],
      EMBEDDED_RUNTIME_API_ORIGINS,
      normalizeRuntimeApiBaseUrl,
    ),
  ).filter((item) => !excludedOrigins.has(item.origin));
}

function currentForumCandidates(excludedOrigins: ReadonlySet<string> = new Set()) {
  return mergeCandidates(
    normalizeRuntimeOrigin,
    EMBEDDED_RUNTIME_FORUM_ORIGINS.map((origin) => ({ origin, priority: EMBEDDED_FALLBACK_PRIORITY })),
    [{ origin: state.initialForumOrigin, priority: EMBEDDED_FALLBACK_PRIORITY }],
    filterBackendCandidates(
      state.manifest?.forum_origins ?? [],
      EMBEDDED_RUNTIME_FORUM_ORIGINS,
      normalizeRuntimeOrigin,
    ),
  ).filter((item) => !excludedOrigins.has(item.origin));
}

function commitSelectedApiOrigin(
  selectedApi: string | undefined,
  forumCandidates: RuntimeEndpointCandidate[],
) {
  if (!selectedApi) return;
  state.selectedApiOrigin = selectedApi;
  const selectedSiteOrigin = new URL(selectedApi).origin;
  // API 与页面必须跟随同一域名；/api 仅是选中站点 origin 下的固定业务前缀。
  if (forumCandidates.some((candidate) => candidate.origin === selectedSiteOrigin)) {
    state.selectedForumOrigin = selectedSiteOrigin;
  }
}

function publishRuntimeEndpointSelection() {
  syncNativeTrustedForumOrigins();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(RUNTIME_ENDPOINTS_CHANGE_EVENT));
  }
}

async function refreshRuntimeEndpointsInternal(excludedOrigins: Set<string>) {
  await readyPromise;
  if (!state.dynamicSelection) return;
  // 只有预埋、初始或已缓存的 API 域名可用于发现新清单，论坛域名不参与配置发现。
  const discoveryOrigins = mergeCandidates(
    normalizeRuntimeApiBaseUrl,
    EMBEDDED_RUNTIME_API_ORIGINS.map((origin) => ({ origin, priority: EMBEDDED_FALLBACK_PRIORITY })),
    [{ origin: state.initialApiOrigin, priority: EMBEDDED_FALLBACK_PRIORITY }],
    filterBackendCandidates(
      state.manifest?.api_origins ?? [],
      EMBEDDED_RUNTIME_API_ORIGINS,
      normalizeRuntimeApiBaseUrl,
    ),
  ).filter((item) => !excludedOrigins.has(item.origin));

  const reusableStartupProbes = startupProbes?.filter(
    (probe) => discoveryOrigins.some((candidate) => candidate.origin === probe.origin),
  ) ?? [];
  startupProbes = undefined;
  const alreadyProbed = new Set(reusableStartupProbes.map((probe) => probe.origin));
  const discovered = [
    ...reusableStartupProbes,
    ...await probeCandidates(
      discoveryOrigins.filter((candidate) => !alreadyProbed.has(candidate.origin)),
    ),
  ];
  if (!state.dynamicSelection) return;
  const knownProbes = new Map(discovered.map((item) => [item.origin, item]));
  // 先严格只用本地候选集选出当前线路，再接管该线路返回的最新动态清单。
  const selectedDiscoveryOrigin = pickHealthyRuntimeEndpoint(
    discoveryOrigins,
    knownProbes,
    state.selectedApiOrigin,
  );
  commitSelectedApiOrigin(selectedDiscoveryOrigin, currentForumCandidates(excludedOrigins));
  const latestManifest = selectedDiscoveryOrigin
    ? knownProbes.get(selectedDiscoveryOrigin)?.manifest
    : undefined;
  // 最新后台清单会替换旧动态候选；发布包预埋和打包入口每次都重新合并，不受后台删改。
  if (latestManifest) state.manifest = mergeManifestWithLocalFallbacks(latestManifest);
  if (!state.manifest) return;

  const apiCandidates = currentApiCandidates(excludedOrigins);
  const forumCandidates = currentForumCandidates(excludedOrigins);

  const selectedApi = await electRuntimeEndpoint(
    apiCandidates,
    knownProbes,
    state.selectedApiOrigin,
  );
  commitSelectedApiOrigin(selectedApi, forumCandidates);
  state.measuredAt = Date.now();
  publishRuntimeEndpointSelection();
  persistRuntimeEndpointCache();
}

interface RuntimeEndpointProbe {
  origin: string;
  latency: number;
  manifest: RuntimeEndpointManifest;
}

async function electRuntimeEndpoint(
  candidates: RuntimeEndpointCandidate[],
  knownProbes: Map<string, RuntimeEndpointProbe>,
  currentOrigin: string,
) {
  const missing = candidates.filter(
    (candidate) => !knownProbes.has(candidate.origin),
  );
  for (const probe of await probeCandidates(missing))
    knownProbes.set(probe.origin, probe);
  return pickHealthyRuntimeEndpoint(candidates, knownProbes, currentOrigin);
}

function pickHealthyRuntimeEndpoint(
  candidates: RuntimeEndpointCandidate[],
  knownProbes: Map<string, RuntimeEndpointProbe>,
  currentOrigin: string,
) {
  const healthy = candidates
    .map((candidate) => ({
      ...candidate,
      latency: knownProbes.get(candidate.origin)?.latency,
    }))
    .filter(
      (
        candidate,
      ): candidate is RuntimeEndpointCandidate & { latency: number } =>
        typeof candidate.latency === "number",
    )
    .sort(
      (left, right) =>
        left.priority - right.priority || left.latency - right.latency,
    );
  if (!healthy.length) return undefined;
  const current = healthy.find(
    (candidate) => candidate.origin === currentOrigin,
  );
  const winner = healthy[0];
  // 当前线路同属最高优先级且仍健康时保持粘性，避免微小延迟波动造成频繁切域。
  return current && current.priority === winner.priority
    ? current.origin
    : winner.origin;
}

async function probeCandidates(candidates: RuntimeEndpointCandidate[]) {
  const results: RuntimeEndpointProbe[] = [];
  // 限制并发避免后台误配大量候选时把移动端连接池和弱网带宽瞬间占满。
  for (
    let offset = 0;
    offset < candidates.length;
    offset += MAX_PARALLEL_PROBES
  ) {
    const batch = candidates.slice(offset, offset + MAX_PARALLEL_PROBES);
    const settled = await Promise.allSettled(
      batch.map((candidate) => probeRuntimeEndpoint(candidate.origin)),
    );
    results.push(
      ...settled.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      ),
    );
  }
  return results;
}

async function probeRuntimeEndpoint(
  origin: string,
): Promise<RuntimeEndpointProbe> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(
    () => controller.abort(),
    DISCOVERY_TIMEOUT_MS,
  );
  const startedAt = performance.now();
  try {
    const response = await fetch(`${origin}${RUNTIME_ENDPOINT_PATH}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    if (!response.ok)
      throw new Error(`runtime endpoint HTTP ${response.status}`);
    const body = (await response.json()) as {
      code?: number;
      data?: RuntimeEndpointManifest;
    };
    if (body.code !== 0 || !body.data || !validateManifest(body.data)) {
      throw new Error("runtime endpoint response is invalid");
    }
    return {
      origin,
      latency: Math.max(1, performance.now() - startedAt),
      manifest: body.data,
    };
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

function validateManifest(payload: RuntimeEndpointManifest) {
  return (
    Number.isSafeInteger(payload?.version) &&
    payload.version >= 1 &&
    validCandidates(payload.api_origins, normalizeRuntimeApiBaseUrl, 1000, MAX_BACKEND_CANDIDATES) &&
    validCandidates(payload.forum_origins, normalizeRuntimeOrigin, 1000, MAX_BACKEND_CANDIDATES)
  );
}

function validateCachedManifest(payload: RuntimeEndpointManifest) {
  return (
    Number.isSafeInteger(payload?.version) &&
    payload.version >= 1 &&
    validCandidates(
      payload.api_origins,
      normalizeRuntimeApiBaseUrl,
      EMBEDDED_FALLBACK_PRIORITY,
      MAX_CACHED_CANDIDATES,
    ) &&
    validCandidates(
      payload.forum_origins,
      normalizeRuntimeOrigin,
      EMBEDDED_FALLBACK_PRIORITY,
      MAX_CACHED_CANDIDATES,
    )
  );
}

function validCandidates(
  candidates: RuntimeEndpointCandidate[],
  normalize: (value: string) => string | undefined,
  maximumPriority: number,
  maximumCandidates: number,
) {
  if (!Array.isArray(candidates) || candidates.length > maximumCandidates) return false;
  const normalized = candidates.map((candidate) => normalize(candidate?.origin));
  return (
    normalized.every(Boolean) &&
    new Set(normalized).size === normalized.length &&
    candidates.every(
      (candidate) =>
        Number.isInteger(candidate.priority) &&
        candidate.priority >= 1 &&
        candidate.priority <= maximumPriority,
    )
  );
}

function mergeManifestWithLocalFallbacks(
  manifest: RuntimeEndpointManifest,
): RuntimeEndpointManifest {
  return {
    version: manifest.version,
    api_origins: mergeCandidates(
      normalizeRuntimeApiBaseUrl,
      EMBEDDED_RUNTIME_API_ORIGINS.map((origin) => ({ origin, priority: EMBEDDED_FALLBACK_PRIORITY })),
      [{ origin: state.initialApiOrigin, priority: EMBEDDED_FALLBACK_PRIORITY }],
      filterBackendCandidates(
        manifest.api_origins,
        EMBEDDED_RUNTIME_API_ORIGINS,
        normalizeRuntimeApiBaseUrl,
      ),
    ),
    forum_origins: mergeCandidates(
      normalizeRuntimeOrigin,
      EMBEDDED_RUNTIME_FORUM_ORIGINS.map((origin) => ({ origin, priority: EMBEDDED_FALLBACK_PRIORITY })),
      [{ origin: state.initialForumOrigin, priority: EMBEDDED_FALLBACK_PRIORITY }],
      filterBackendCandidates(
        manifest.forum_origins,
        EMBEDDED_RUNTIME_FORUM_ORIGINS,
        normalizeRuntimeOrigin,
      ),
    ),
  };
}

function mergeCandidates(
  normalize: (value: string) => string | undefined,
  ...groups: Array<readonly RuntimeEndpointCandidate[]>
) {
  const byOrigin = new Map<string, RuntimeEndpointCandidate>();
  for (const group of groups) {
    for (const candidate of group) {
      const origin = normalize(candidate.origin);
      if (!origin) continue;
      const current = byOrigin.get(origin);
      if (!current || candidate.priority < current.priority) {
        byOrigin.set(origin, { origin, priority: candidate.priority });
      }
    }
  }
  return [...byOrigin.values()].sort(
    (left, right) =>
      left.priority - right.priority || left.origin.localeCompare(right.origin),
  );
}

function filterBackendCandidates(
  candidates: readonly RuntimeEndpointCandidate[],
  embeddedOrigins: readonly string[],
  normalize: (value: string) => string | undefined,
) {
  const embedded = new Set(
    embeddedOrigins
      .map((origin) => normalize(origin))
      .filter((origin): origin is string => Boolean(origin)),
  );
  // 后台只允许补充候选，不能通过同 origin 条目修改发布包内预埋域名的固定属性。
  return candidates.filter((candidate) => {
    const origin = normalize(candidate.origin);
    return Boolean(origin && !embedded.has(origin));
  });
}

function normalizeRuntimeApiBaseUrl(value: string) {
  try {
    const url = new URL(value.trim());
    if (
      (url.protocol !== "https:" && !isLoopbackHost(url.hostname)) ||
      url.username ||
      url.password ||
      url.search ||
      url.hash
    )
      return undefined;
    // API 候选保留反向代理前缀，并统一移除尾斜杠，业务请求可以直接安全追加固定路由。
    return `${url.origin}${url.pathname.replace(/\/+$/, "")}`;
  } catch {
    return undefined;
  }
}

function normalizeRuntimeOrigin(value: string) {
  try {
    const url = new URL(value.trim());
    if (
      (url.protocol !== "https:" && !isLoopbackHost(url.hostname)) ||
      url.username ||
      url.password
    )
      return undefined;
    if ((url.pathname !== "/" && url.pathname !== "") || url.search || url.hash)
      return undefined;
    return url.origin;
  } catch {
    return undefined;
  }
}

function normalizeForumBaseUrl(value: string) {
  try {
    const url = new URL(value.trim());
    if (
      (url.protocol !== "https:" && !isLoopbackHost(url.hostname)) ||
      url.username ||
      url.password ||
      url.search ||
      url.hash
    )
      return undefined;
    return url.toString().replace(/\/$/, "");
  } catch {
    return undefined;
  }
}

function isLoopbackHost(hostname: string) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1"
  );
}

function readRuntimeEndpointCache(): RuntimeEndpointCache | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw =
      window.localStorage.getItem(RUNTIME_ENDPOINT_CACHE_KEY) ??
      window.localStorage.getItem(LEGACY_RUNTIME_ENDPOINT_CACHE_KEY);
    return raw ? (JSON.parse(raw) as RuntimeEndpointCache) : undefined;
  } catch {
    return undefined;
  }
}

function persistRuntimeEndpointCache() {
  if (typeof window === "undefined" || !state.manifest) return;
  try {
    const cached: RuntimeEndpointCache = {
      manifest: state.manifest,
      selectedApiOrigin: state.selectedApiOrigin,
      selectedForumOrigin: state.selectedForumOrigin,
      measuredAt: state.measuredAt,
    };
    window.localStorage.setItem(
      RUNTIME_ENDPOINT_CACHE_KEY,
      JSON.stringify(cached),
    );
  } catch {
    // 禁用本地存储只会失去快速恢复能力，当前内存中的选举结果仍然有效。
  }
}
